import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../../../components/general/Icon';
import { Label } from '../../../components/general/forms/Label';
import { Colors } from '../../../styles/colors';
import { IconDefinitions } from '../../../utils/types/icon-definitions';
import { ErrorText } from "./DefaultInput";

export interface SelectBoxOptionProps {
  key: string;
  value: string;
  icon: string
}

export interface SelectBoxProps {
  id: string;
  label?: string;
  onChange: (data: string[]) => void;
  placeholder?: string;
  type?: string;
  value?: string[];
  options: SelectBoxOptionProps[];
  multi?: boolean;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  hasError?: boolean;
}

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 2rem;
`;

export const InputInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  overflow: hidden;
`;

export const StyledSelectBoxWrapper: any = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  
  &:last-child {
    margin-right: 0;
  }

  &.disabled {
    pointer-events: none;
    cursor: default;
  }
`;
const StyledSelectBoxOption = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  background: ${Colors.background};
  margin-bottom: .5rem;
  
  &:hover {
    cursor: pointer;
    
    @media (pointer: fine) {
      color: ${Colors.primaryLight};
    }
  }
  
  &.active {
   color: ${Colors.white};
   background: linear-gradient(to bottom, #9DCFF3 -100%, #3B69B0 120%);
  }
`;

const StyledSelectBoxOptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-right: 2rem;
`;

export const SelectBox: FC<SelectBoxProps> = (props) => {
  const { value, required = false, disabled = false, errorMessage = 'Bitte treffen Sie eine Wahl...', hasError = false } = props;
  const [localError, setLocalError] = useState<boolean>(hasError);

  useEffect(() => {
    setLocalError(hasError);
  }, [hasError]);

  useEffect(() => {
    if (value && value.length > 0) {
      setLocalError(false);
    }
  }, [value]);

  const onChangeListener = (option: SelectBoxOptionProps, checked?: boolean) => {
    if (props.multi) {
      if (value && value.includes(option.key)) {
        const data = value.filter((value) => value !== option.key) || [];
        props.onChange(data);
        if (data.length === 0) {
          setLocalError(true);
        }
      } else {
        setLocalError(false);
        props.onChange([...value, option.key]);
      }
    } else {
      if (checked) {
        setLocalError(false);
        props.onChange([option.key]);
      } else {
        setLocalError(true);
        props.onChange([]);
      }
    }
  };

  return (
    <InputWrapper>
      <Label for={props.id}>{props.label}</Label>
      <InputInnerWrapper>
        <StyledSelectBoxWrapper className={disabled ? 'disabled' : ''}>
          {
            props?.options?.map((option, index) => {
              return (
                <StyledSelectBoxOptionWrapper key={index}>
                  <input
                    type={'checkbox'}
                    value={option.key}
                    style={{ visibility: 'hidden' }}
                    disabled={disabled}
                    checked={props.value?.includes(option.key)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      onChangeListener(option, event.target.checked);
                    }}
                  />
                  <StyledSelectBoxOption
                    className={`${props.value?.includes(option.key) ? 'active' : ''}`}
                    data-key={option.key}
                    data-value={option.value}
                    key={option.value}
                    onClick={() => {
                      onChangeListener(option, !props.value?.includes(option.key));
                    }}
                  >
                    <Icon icon={option.icon as IconDefinitions} size={64}
                      color={`${localError ? Colors.error : 'inherit'}`} />
                  </StyledSelectBoxOption>
                  {option.value}
                </StyledSelectBoxOptionWrapper>
              );
            })
          }
        </StyledSelectBoxWrapper>
        {
          localError && <ErrorText>{errorMessage}</ErrorText>
        }
      </InputInnerWrapper>
    </InputWrapper>
  );
};
